/**
 * ./preferensi bound to the browser (plan 9b, 5.4): the API (`/akun/preferensi`, always the
 * session's own account), the session store, the device store and `localStorage`.
 * `pasangPreferensiAkun` is installed once by auth/app `startSession`, before the session is
 * read: each sign-in, unlock or change of person loads that person's preferences. Akun's
 * Tampilan changes them through `ubahTampilanAkun`.
 */
import { jawabanPreferensiSchema } from '@satu/shared/preferensi'
import { apiFetch } from '../../api/client'
import { laporkanGalat } from '../pelaporan'
import { useDevice } from './device'
import { muatPreferensi, type PreferensiDeps, perluMuat, ubahTampilan } from './preferensi'
import { PAROKI_CACHE_KEY, useSesi } from './sesi'
import {
  browserStorage,
  catatTampilanSebelum,
  type StorageLike,
  type Tampilan,
  tampilanPerangkat,
} from './tampilan'

const JALUR = '/akun/preferensi'

/** index.html's device class: a coarse pointer, or narrower than 1024 px. */
function kasarAtauSempit(): boolean {
  if (typeof matchMedia !== 'function') return false
  return matchMedia('(pointer: coarse)').matches || window.innerWidth < 1024
}

function depsBrowser(): PreferensiDeps {
  return {
    storage: browserStorage(),
    ambil: async () => jawabanPreferensiSchema.parse(await apiFetch<unknown>(JALUR)),
    simpan: async (preferensi) =>
      jawabanPreferensiSchema.parse(
        await apiFetch<unknown>(JALUR, { method: 'PATCH', body: preferensi }),
      ),
    terapkan: (pref) => useDevice.getState().terapkan(pref),
    penggunaLayar: () => {
      const { status, sesi } = useSesi.getState()
      return status === 'aktif' ? sesi.userId : ''
    },
    perangkat: () => tampilanPerangkat(kasarAtauSempit()),
  }
}

/** Whether a session was ever opened on this device: the splash's parish cache (store/sesi). */
function pernahMasuk(storage: StorageLike | null): boolean {
  if (storage === null) return false
  try {
    return storage.getItem(PAROKI_CACHE_KEY) !== null
  } catch {
    return false
  }
}

let terpasang = false

export function pasangPreferensiAkun(): void {
  if (terpasang) return
  terpasang = true
  // Before anyone signs in: what a device from before plan 9b showed stays for every account
  // without its own (./tampilan `catatTampilanSebelum`).
  const storage = browserStorage()
  catatTampilanSebelum(storage, pernahMasuk(storage))
  useSesi.subscribe((kini, sebelum) => {
    if (!perluMuat(kini, sebelum)) return
    // Whatever fails in a load, the device keeps its copy; the failure is reported, never left
    // as an unhandled rejection.
    muatPreferensi(kini.sesi.userId, depsBrowser()).catch((error: unknown) => laporkanGalat(error))
  })
}

/** Akun › Tampilan: one change, for the signed-in person's account. */
export function ubahTampilanAkun(patch: Partial<Tampilan>): Promise<Tampilan> {
  const { density, text, motion } = useDevice.getState()
  return ubahTampilan(
    useSesi.getState().sesi.userId,
    { density, text, motion },
    patch,
    depsBrowser(),
  )
}
